import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Notification from "../../component/notification";
import { doc, setDoc, serverTimestamp,  getDocs, collection, getDoc,deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { v4 as uuidv4 } from 'uuid';

const predefinedCategories = [
  "Web Development",
  "Mobile App Development",
  "UI/UX Design",
  "Data Science",
  "Cybersecurity",
];

const suggestedDurations = [15, 30, 45, 60, 90];

const AddCourseForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const initialValues = location.state?.course;
  const [title, setTitle] = useState(initialValues?.title || "");
  const [category, setCategory] = useState(initialValues?.category || "");
  const [description, setDescription] = useState(initialValues?.description || "");
  const [duration, setDuration] = useState(initialValues?.duration || "");
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const titleRef = useRef(null);
  const [categories, setCategories] = useState([]);

  const validate = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "Course title is required.";
    if (!category.trim()) newErrors.category = "Category is required.";
    if (!description.trim()) newErrors.description = "Description is required.";
    if (!duration || isNaN(duration) || Number(duration) < 1) newErrors.duration = "Duration must be a positive number.";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
  
    setLoading(true);
    try {
      const oldDocId = initialValues?.id;
      const newDocId = `${title.trim()}-${uuidv4()}`;
      const docRef = doc(db, 'courses', newDocId);
  
      if (oldDocId && oldDocId !== newDocId) {
        const oldDocRef = doc(db, 'courses', oldDocId);
        const oldDocSnap = await getDoc(oldDocRef);
        await setDoc(docRef, {
          ...oldDocSnap.data(),
          title: title.trim(),
          category: category.trim(),
          description: description.trim(),
          duration: Number(duration),
          updatedAt: serverTimestamp()
        });
        await deleteDoc(oldDocRef);
      } else {
        await setDoc(docRef, {
          title: title.trim(),
          category: category.trim(),
          description: description.trim(),
          duration: Number(duration),
          [oldDocId ? 'updatedAt' : 'createdAt']: serverTimestamp()
        }, { merge: true });
      }
  
      setNotification({ 
        show: true, 
        message: `Course ${oldDocId ? 'updated' : 'added'} successfully!`, 
        type: 'success' 
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
        setLoading(false);
        navigate('/course');
      }, 2000);
    } catch (error) {
      setNotification({ 
        show: true, 
        message: `Error: ${error.message}`, 
        type: 'failure' 
      });
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/course");
  };

  useEffect(() => {
    if (titleRef.current) titleRef.current.focus();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'courseCategories'));
        const categoriesData = querySnapshot.docs.map(doc => doc.data().title);
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories: ", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'courses'));
        const coursesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCourses(coursesData);
      } catch (error) {
        // Handle error
      }
    };
    fetchCourses();
  }, []);

  return (
    <div className="border rounded shadow-sm p-4 bg-white" style={{ maxWidth: 600, margin: "2rem auto" }}>
      <form onSubmit={handleSubmit} className="container" style={{ maxWidth: 500 }} autoComplete="off">
        <h3 className="mb-4 text-center fw-bold" style={{ letterSpacing: 1 }}>{initialValues ? "Update Course" : "Add New Course"}</h3>
        {notification.show && (
          <Notification message={notification.message} type={notification.type} onClose={() => setNotification({ ...notification, show: false })} />
        )}
        <div className="mb-3">
          <label htmlFor="courseTitle" className="form-label">Course Title</label>
          <input
            id="courseTitle"
            type="text"
            className={`form-control${errors.title ? " is-invalid" : ""}`}
            placeholder="e.g., Introduction to React"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            ref={titleRef}
            autoFocus
          />
          {errors.title && <div className="invalid-feedback">{errors.title}</div>}
        </div>
        <div className="mb-3">
          <label htmlFor="courseDuration" className="form-label">Duration of Internship (Days)</label>
          <div className="input-group">
            <input
              id="courseDuration"
              type="number"
              className={`form-control${errors.duration ? " is-invalid" : ""}`}
              placeholder="Enter number of days"
              value={duration}
              min={1}
              onChange={(e) => setDuration(e.target.value)}
            />
            <button
              className="btn btn-outline-secondary dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              tabIndex={-1}
            >
              Days
            </button>
            <ul className="dropdown-menu">
              {suggestedDurations.map((d, idx) => (
                <li key={idx}>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => setDuration(d)}
                  >
                    {d} days
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="form-text">Choose or enter the number of days for the internship.</div>
          {errors.duration && <div className="invalid-feedback d-block">{errors.duration}</div>}
        </div>
        <div className="mb-3">
          <label htmlFor="courseCategory" className="form-label">Category</label>
          <select
            id="courseCategory"
            className={`form-control${errors.category ? " is-invalid" : ""}`}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="" disabled>Select a category</option>
            {categories.map((cat, idx) => (
              <option key={idx} value={cat}>{cat}</option>
            ))}
          </select>
          {errors.category && <div className="invalid-feedback d-block">{errors.category}</div>}
        </div>
        <div className="mb-3">
          <label htmlFor="courseDescription" className="form-label">Description</label>
          <textarea
            id="courseDescription"
            className={`form-control${errors.description ? " is-invalid" : ""}`}
            placeholder="Describe the course..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
          {errors.description && <div className="invalid-feedback d-block">{errors.description}</div>}
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-dark" disabled={loading}>
            {loading ? <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> : null}
            {initialValues ? "Update" : "Add"}
          </button>
        </div>
      </form>
    </div>
);
};

export default AddCourseForm;